import React, { useState, useRef, useEffect, useCallback } from 'react'
import Cookies from 'js-cookie'
import { useNavigate, useLocation } from 'react-router-dom'
import { API_URL } from '../../config'
import { S } from './styles'
import AitimeLogo from '../../assets/icon/aiviz_icon.png'
import RobotMascot from './RobotMascot'
import Message from './Message'
import { TT_NORMAL_STEPS, TT_FLEXIBLE_STEPS } from './timetableSteps'
import { SCHEDULE_EMP_STEPS, SCHEDULE_BRANCH_STEPS, stepLabel } from './scheduleSteps'
import { BREAKTIME_STEPS } from './breaktimeSteps'
import { TEMPSCHEDULE_STEPS } from './tempScheduleSteps'
import {
  APPROVAL_GUIDES,
  CONFIG_GUIDES,
  EMPLOYEE_GUIDES,
  REPORT_CATEGORY_PAGES,
  WORKCODE_GUIDES,
} from './menuData'
import { PAYROLL_FLOW_GUIDE, PAYROLL_GUIDES, PAYROLL_ISSUE_STEPS } from './payrollData'
import { LEAVE_FLOW_GUIDE, LEAVE_GUIDES, LEAVE_ISSUE_STEPS } from './leaveData'

const STORAGE_KEY = 'chatbot_history'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// Predefined suggestion phrases — each maps to a real intent/handler
const SUGGESTIONS = [
  // Attendance — daily status queries
  "Who's late today?",
  'Who is absent today?',
  'Who is present today?',
  'Who left early today?',
  'Who has missing punch today?',
  'Show employees with exceptions today',
  'Show unscheduled employees today',
  'Who was absent yesterday?',
  'Who was late yesterday?',
  // Employees — search & info
  'Total employee count',
  'Employee strength',
  'Show employees',
  'Show employees in Finance',
  'Employees in HR department',
  'Who works in Sales?',
  'Who works as Manager?',
  'Find Ahmed',
  'Show profile of Ahmed',
  'Tell me about Nizar',
  'Where does Ahmed work?',
  'Ahmed work location',
  'Department of Ahmed',
  'When did Ahmed join?',
  'Joining date of Nizar',
  'Phone number of Ahmed',
  'Mobile of Nizar',
  'Email of Ahmed',
  'Contact details of EMP001',
  'Manager of Finance',
  'Who is the team lead of HR?',
  'Ahmed reporting officer',
  'How to add an employee?',
  'How to edit an employee?',
  'List all departments',
  'List all designations',
  'List all areas',
  'Area codes',
  'Employees without schedule',
  'Employee count by department',
  'Who has birthday today?',
  'Who is on leave today?',
  // Leave
  'Leave',
  'Leave status',
  'How to set up leaves?',
  // Payroll
  'Payroll',
  'How to set up payroll?',
  // Approvals
  'Approvals',
  // Reports
  'Reports',
  // Scheduling
  'Show timetables',
  'Create timetable',
  'How to create a timetable?',
  'How to create a normal timetable?',
  'How to create a flexible timetable?',
  'Show shifts',
  'Create shift',
  'How to create a shift?',
  'Assign schedule',
  'How to create a schedule?',
  'Create break time',
  'How to create a break time?',
  'Temporary schedule',
  'How to create a temporary schedule?',
  // Other modules
  'Holiday',
  'How to create a holiday?',
  'Work from home',
  'How to create a work from home request?',
  'Recalculation',
  'How to recalculate attendance?',
  'Manual attendance',
  'How to add manual attendance?',
  'Leave types',
  'How to create a leave type?',
  'Work codes',
  'Configurations',
  'Departments',
  'Designations',
  // Help
  'Help',
]

const INITIAL_MESSAGE = {
  role: 'bot',
  text: "Hi, I'm AITIME Assistant! I can answer questions about employees, attendance, leaves, timetables, and schedules. Type **help** to see examples, or type **reports** to browse all reports.",
  data: null,
  link: null,
  options: null,
}

const BUBBLE_POS_KEY = 'chatbot_bubble_pos'
const MAX_OFFSET = 160

function clampOffset(x, y) {
  return {
    x: Math.max(-MAX_OFFSET, Math.min(0, x)),
    y: Math.max(0, Math.min(MAX_OFFSET, y)),
  }
}

export default function ChatbotWidget() {
  const [chatbotEnabled, setChatbotEnabled] = useState(null)
  const [open, setOpen] = useState(false)
  const [bubbleOffset, setBubbleOffset] = useState(() => {
    try {
      const saved = localStorage.getItem(BUBBLE_POS_KEY)
      if (saved) {
        const p = JSON.parse(saved)
        return clampOffset(p.x, p.y)
      }
    } catch (_) {}
    return { x: 0, y: 0 }
  })
  const dragRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const bubbleOffsetRef = useRef(bubbleOffset)
  bubbleOffsetRef.current = bubbleOffset

  const onBubblePointerDown = useCallback((e) => {
    if (e.button !== 0) return
    e.preventDefault()
    const cur = bubbleOffsetRef.current

    const onMove = (ev) => {
      const dx = ev.clientX - dragRef.current.startX
      const dy = ev.clientY - dragRef.current.startY
      if (!dragRef.current.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return
      if (!dragRef.current.moved) {
        dragRef.current.moved = true
        setDragging(true)
      }
      const next = clampOffset(dragRef.current.origX + dx, dragRef.current.origY - dy)
      setBubbleOffset(next)
    }

    const onUp = (ev) => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onUp)
      const wasMoved = dragRef.current?.moved
      setDragging(false)
      if (wasMoved) {
        const final = bubbleOffsetRef.current
        try {
          localStorage.setItem(BUBBLE_POS_KEY, JSON.stringify(final))
        } catch (_) {}
        ev.stopPropagation()
        dragRef.current = null
      } else {
        dragRef.current = null
        setOpen((v) => !v)
      }
    }

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: cur.x,
      origY: cur.y,
      moved: false,
    }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onUp)
  }, [])
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const msgs = JSON.parse(saved)
        // Strip trailing bot error messages left by failed/aborted requests
        while (
          msgs.length > 1 &&
          msgs[msgs.length - 1].role === 'bot' &&
          msgs[msgs.length - 1].text?.startsWith('Error:')
        ) {
          msgs.pop()
        }
        return msgs.length ? msgs : [INITIAL_MESSAGE]
      }
    } catch (_) {}
    return [INITIAL_MESSAGE]
  })
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [suggestionIdx, setSuggestionIdx] = useState(-1)
  const [listening, setListening] = useState(false)
  const [voiceSupported] = useState(() => {
    const supported = !!(window.SpeechRecognition || window.webkitSpeechRecognition)
    if (!supported) console.warn('[AITIME] SpeechRecognition not available — mic button hidden')
    return supported
  })
  const recognitionRef = useRef(null)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const wizardRef = useRef(null)
  const awaitingBriefRef = useRef(null) // set to { messages } while waiting for user to describe their issue
  const supportMailRef = useRef(null) // { step: 'brief'|'name'|'phone', snapshotMsgs, brief?, name? }
  const permsRef = useRef({
    canCreateSchedule: false,
    canCreateShift: false,
    canCreateTimetable: false,
    canCreateBreaktime: false,
    canCreateTempschedule: false,
    canAccessHoliday: false,
    canAddHoliday: false,
    canAccessLeaveType: false,
    canAccessLeaveOff: false,
    canAddLeaveOff: false,
    canAccessWfh: false,
    canAddWfh: false,
    canAccessRecalculation: false,
    canAddRecalculation: false,
    canAccessTransfer: false,
    canAccessDailyActivity: false,
    canAccessFirstLast: false,
    canAccessMultiWorkedHours: false,
    canAccessWorkcodeReport: false,
    canAccessDetailedReport: false,
    canAccessMonthlyStatus: false,
    canAccessMonthlySummary: false,
    canAccessMonthlyOvertime: false,
    canAccessMonthlyAbsence: false,
    canAccessLeaveSummary: false,
    canAccessLeaveReport: false,
    canAccessDetailedLeave: false,
    canAccessLateInReport: false,
    canAccessEarlyOutReport: false,
    canAccessTransactions: false,
  })

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
    } catch (_) {}
  }, [messages])

  // Reset chat on logout (when route becomes /login)
  useEffect(() => {
    if (location.pathname === '/login') {
      setMessages([INITIAL_MESSAGE])
      setInput('')
      setOpen(false)
      wizardRef.current = null
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch (_) {}
    }
  }, [location.pathname])

  // Fetch user permissions once on mount and on each login
  useEffect(() => {
    const token = Cookies.get('access_token') || ''
    if (!token) return
    fetch(`${API_URL}/chatbot/user-info/`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .catch(() => ({}))
      .then((info) => {
        setChatbotEnabled(!!info.chatbot_enabled)
        permsRef.current = {
          canCreateSchedule: !!info.can_create_schedule,
          canCreateShift: !!info.can_create_shift,
          canCreateTimetable: !!info.can_create_timetable,
          canCreateBreaktime: !!info.can_create_breaktime,
          canCreateTempschedule: !!info.can_create_tempschedule,
          canAccessHoliday: !!info.can_access_holiday,
          canAddHoliday: !!info.can_add_holiday,
          canAccessLeaveType: !!info.can_access_leave_type,
          canAccessLeaveOff: !!info.can_access_leave_off,
          canAddLeaveOff: !!info.can_add_leave_off,
          canAccessWfh: !!info.can_access_wfh,
          canAddWfh: !!info.can_add_wfh,
          canAccessRecalculation: !!info.can_access_recalculation,
          canAddRecalculation: !!info.can_add_recalculation,
          canAccessTransfer: !!info.can_access_transfer,
          canAccessDailyActivity: !!info.can_access_daily_activity,
          canAccessFirstLast: !!info.can_access_first_last,
          canAccessMultiWorkedHours: !!info.can_access_multi_worked_hours,
          canAccessWorkcodeReport: !!info.can_access_workcode_report,
          canAccessDetailedReport: !!info.can_access_detailed_report,
          canAccessMonthlyStatus: !!info.can_access_monthly_status,
          canAccessMonthlySummary: !!info.can_access_monthly_summary,
          canAccessMonthlyOvertime: !!info.can_access_monthly_overtime,
          canAccessMonthlyAbsence: !!info.can_access_monthly_absence,
          canAccessLeaveSummary: !!info.can_access_leave_summary,
          canAccessLeaveReport: !!info.can_access_leave_report,
          canAccessDetailedLeave: !!info.can_access_detailed_leave,
          canAccessLateInReport: !!info.can_access_late_in_report,
          canAccessEarlyOutReport: !!info.can_access_early_out_report,
          canAccessTransactions: !!info.can_access_transactions,
        }
      })
  }, [location.pathname])

  // Scroll to bottom when messages change or panel is opened
  useEffect(() => {
    if (!open) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading, open])

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80)
    }
  }, [open])

  const handleNavigate = useCallback(
    (path) => {
      navigate(path)
      setOpen(false)
    },
    [navigate],
  )

  // ─── Timetable wizard helpers ───────────────────────────────────────────

  // Always-fresh ref to wizard step handler (avoids stale closure in sendMessage)
  const _advanceWizardRef = useRef(null)

  // Called on each render — keeps _advanceWizardRef.current up-to-date
  _advanceWizardRef.current = async function advanceWizard(text) {
    const w = wizardRef.current
    if (!w) return
    const { steps, stepIdx, data } = w
    const step = steps[stepIdx]
    const val = text.trim()

    // ── Date format validation (schedule wizard) ──
    if (['start_date', 'end_date'].includes(step.key) && val !== 'skip') {
      let dateInvalid = !/^\d{4}-\d{2}-\d{2}$/.test(val)
      if (!dateInvalid) {
        const [y, m, d] = val.split('-').map(Number)
        const dt = new Date(y, m - 1, d)
        dateInvalid = dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d
      }
      if (dateInvalid) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: 'Invalid date. Please enter a real date as **YYYY-MM-DD** (e.g. 2026-06-01).',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }
    }

    // ── Employee ID live validation (schedule wizard) ──
    if (step.key === 'employee_id') {
      setLoading(true)
      try {
        const token = Cookies.get('access_token') || ''
        const res = await fetch(
          `${API_URL}/chatbot/validate/?type=employee&value=${encodeURIComponent(val)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        const json = await res.json().catch(() => ({}))
        if (!json.valid) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text: json.error || `Employee ID "${val}" not found. Please check and try again.`,
              data: null,
              link: null,
              options: null,
            },
          ])
          setLoading(false)
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Found employee: **${json.name}**. \u2713`,
            data: null,
            link: null,
            options: null,
          },
        ])
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Could not validate employee ID: ${e.message}`,
            data: null,
            link: null,
            options: null,
          },
        ])
        setLoading(false)
        return
      } finally {
        setLoading(false)
      }
    }

    // ── Branch / Dept live validation (schedule wizard) ──
    if (step.key === 'branch') {
      setLoading(true)
      try {
        const token = Cookies.get('access_token') || ''
        const res = await fetch(
          `${API_URL}/chatbot/validate/?type=branch&value=${encodeURIComponent(val)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        const json = await res.json().catch(() => ({}))
        if (!json.valid) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text: json.error || `Branch "${val}" not found. Please check and try again.`,
              data: null,
              link: null,
              options: null,
            },
          ])
          setLoading(false)
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Found branch: **${json.name}**. \u2713`,
            data: null,
            link: null,
            options: null,
          },
        ])
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Could not validate branch: ${e.message}`,
            data: null,
            link: null,
            options: null,
          },
        ])
        setLoading(false)
        return
      } finally {
        setLoading(false)
      }
    }

    // ── Shift live validation (schedule wizard) ──
    if (step.key === 'shift') {
      setLoading(true)
      try {
        const token = Cookies.get('access_token') || ''
        const res = await fetch(
          `${API_URL}/chatbot/validate/?type=shift&value=${encodeURIComponent(val)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        const json = await res.json().catch(() => ({}))
        if (!json.valid) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text:
                json.error ||
                `Shift "${val}" not found. Please check the shift name and try again.`,
              data: null,
              link: null,
              options: null,
            },
          ])
          setLoading(false)
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Found shift: **${json.name}**. \u2713`,
            data: null,
            link: null,
            options: null,
          },
        ])
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Could not validate shift: ${e.message}`,
            data: null,
            link: null,
            options: null,
          },
        ])
        setLoading(false)
        return
      } finally {
        setLoading(false)
      }
    }

    // ── Timetable live validation (temp schedule wizard) ──
    if (step.key === 'timetable') {
      setLoading(true)
      try {
        const token = Cookies.get('access_token') || ''
        const res = await fetch(
          `${API_URL}/chatbot/validate/?type=timetable&value=${encodeURIComponent(val)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        const json = await res.json().catch(() => ({}))
        if (!json.valid) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text:
                json.error || `Timetable "${val}" not found. Please check the name and try again.`,
              data: null,
              link: null,
              options: null,
            },
          ])
          setLoading(false)
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Found timetable: **${json.name}**. \u2713`,
            data: null,
            link: null,
            options: null,
          },
        ])
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Could not validate timetable: ${e.message}`,
            data: null,
            link: null,
            options: null,
          },
        ])
        setLoading(false)
        return
      } finally {
        setLoading(false)
      }
    }

    // Time field validation
    const TIME_KEYS = [
      'check_in',
      'check_out',
      'ci_start',
      'co_start',
      'ci_end',
      'co_end',
      'start_time',
      'end_time',
    ]
    if (TIME_KEYS.includes(step.key) && val !== 'skip' && !/^\d{1,2}:\d{2}$/.test(val)) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: 'Please enter time in HH:MM format (e.g. 09:00), or type "skip" to omit this field.',
          data: null,
          link: null,
          options: null,
        },
      ])
      return
    }
    // Cross-days validation
    if (step.key.includes('cross') && !['0', '1', '2', '3'].includes(val)) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: 'Please enter 0, 1, 2, or 3 for cross days.',
          data: null,
          link: null,
          options: null,
        },
      ])
      return
    }
    // Duration integer validation
    if (step.key === 'duration' && (!/^\d+$/.test(val) || parseInt(val, 10) <= 0)) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: 'Please enter a valid number of minutes (e.g. 60).',
          data: null,
          link: null,
          options: null,
        },
      ])
      return
    }

    // ── Month/Year validation (payroll issue wizard) ──
    if (step.key === 'month_year') {
      if (!/^\d{4}-\d{2}$/.test(val)) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: 'Please enter month/year as **YYYY-MM** (e.g. `2026-05`).',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }
      const [y, m] = val.split('-').map(Number)
      if (m < 1 || m > 12 || y < 2000 || y > 2100) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: 'Invalid month/year. Month must be 01–12 and year must be 2000–2100.',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }
    }

    // ── Year validation (leave issue wizard) ──
    if (step.key === 'year') {
      if (!/^\d{4}$/.test(val)) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: 'Please enter the year as **YYYY** (e.g. `2026`).',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }
      const y = Number(val)
      if (y < 2000 || y > 2100) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: 'Year must be between 2000 and 2100.',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }
    }

    const newData = { ...data, [step.key]: val }

    // Skip conditional steps
    let nextIdx = stepIdx + 1
    while (nextIdx < steps.length && steps[nextIdx].skipIf && steps[nextIdx].skipIf(newData)) {
      nextIdx++
    }

    // ── Dynamic leave type fetch: after year step in leave issue wizard ──
    if (step.key === 'year' && nextIdx < steps.length && steps[nextIdx].key === 'leave_type_name') {
      setLoading(true)
      try {
        const token = Cookies.get('access_token') || ''
        const res = await fetch(
          `${API_URL}/chatbot/leave-types-for-employee/?employee_id=${encodeURIComponent(
            newData.employee_id,
          )}&year=${encodeURIComponent(val)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        const json = await res.json().catch(() => ({}))
        const ltOpts =
          Array.isArray(json.leave_types) && json.leave_types.length
            ? json.leave_types.map((name) => ({ label: name, value: name, type: 'wizard' }))
            : null
        const nextStep = steps[nextIdx]
        wizardRef.current = { ...w, stepIdx: nextIdx, data: newData }
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: ltOpts
              ? nextStep.label
              : 'No leave balance records found for this employee and year. Please check the year or ensure payroll has been run.',
            data: null,
            link: null,
            options: ltOpts,
          },
        ])
        if (!ltOpts) wizardRef.current = null
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Could not fetch leave types: ${e.message}`,
            data: null,
            link: null,
            options: null,
          },
        ])
        wizardRef.current = null
      } finally {
        setLoading(false)
      }
      return
    }

    if (nextIdx >= steps.length) {
      const completeFn = w.onComplete
      wizardRef.current = null
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: completeFn
            ? 'All details collected! Submitting\u2026'
            : 'All details collected! Creating timetable\u2026',
          data: null,
          link: null,
          options: null,
        },
      ])
      if (completeFn) {
        completeFn(newData)
      } else {
        _createTimetableRef.current(newData)
      }
      return
    }

    const nextStep = steps[nextIdx]
    const newWizard = { ...w, stepIdx: nextIdx, data: newData }
    wizardRef.current = newWizard

    // For the shift step: fetch available shifts as clickable options
    if (nextStep.key === 'shift') {
      let shiftOpts = null
      try {
        const token = Cookies.get('access_token') || ''
        const res = await fetch(`${API_URL}/chatbot/validate/?type=shift_list`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const json = await res.json().catch(() => ({}))
        if (Array.isArray(json.shifts) && json.shifts.length) {
          shiftOpts = json.shifts.map((name) => ({ label: name, value: name, type: 'wizard' }))
        }
      } catch (_) {}
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: stepLabel(nextStep), data: null, link: null, options: shiftOpts },
      ])
      return
    }

    const opts = nextStep.options ? nextStep.options.map((o) => ({ ...o, type: 'wizard' })) : null
    setMessages((prev) => [
      ...prev,
      { role: 'bot', text: stepLabel(nextStep), data: null, link: null, options: opts },
    ])
  }

  // Always-fresh ref to timetable creation function
  const _createTimetableRef = useRef(null)
  _createTimetableRef.current = async function createTimetable(data) {
    setLoading(true)
    try {
      const token = Cookies.get('access_token') || ''
      const parseOt = (str) => {
        if (!str) return [0, 0]
        const parts = str.trim().split(/\s+/)
        return [
          Math.round(parseFloat(parts[0] || 0) * 60),
          Math.round(parseFloat(parts[1] || 0) * 60),
        ]
      }
      const [l1from, l1to] = parseOt(data.ot_l1)
      const [l2from, l2to] = parseOt(data.ot_l2)
      const [l3from, l3to] = parseOt(data.ot_l3)
      const dur = parseInt(data.work_duration) || 480

      const body = new URLSearchParams()
      body.append('name', data.name)
      body.append('timetable_type', data.type)
      body.append('check_in_time', data.check_in)
      body.append('check_out_time', data.check_out)
      body.append('flexi_cross_day', data.flexi_cross || '0')
      if (data.ci_start && data.ci_start !== 'skip')
        body.append('check_in_start_time', data.ci_start)
      body.append('check_in_start_cross_day', data.ci_start_cross || '0')
      if (data.co_start && data.co_start !== 'skip')
        body.append('check_out_start_time', data.co_start)
      body.append('check_out_start_cross_day', data.co_start_cross || '0')
      if (data.ci_end) body.append('check_in_end_time', data.ci_end)
      body.append('check_in_end_cross_day', data.ci_end_cross || '0')
      if (data.co_end) body.append('check_out_end_time', data.co_end)
      body.append('check_out_end_cross_day', data.co_end_cross || '0')
      body.append('duration', dur)
      body.append('work_day', dur)
      body.append('work_type', 'N')
      body.append('mt_duration', parseInt(data.ot_max_dur) || 0)
      body.append('calculate_overtime', data.ot_confirm === 'yes' ? 'true' : 'false')
      body.append('calculate_max_ot', data.ot_max_confirm === 'yes' ? 'true' : 'false')
      body.append('overtime_l_one', data.ot_confirm === 'yes' ? 'NO' : 'I')
      body.append('ot_from_l_one', l1from)
      body.append('ot_to_l_one', l1to)
      body.append('overtime_l_two', data.ot_l2_confirm === 'yes' ? 'NO' : 'I')
      body.append('ot_from_l_two', l2from)
      body.append('ot_to_l_two', l2to)
      body.append('overtime_l_three', data.ot_l3_confirm === 'yes' ? 'NO' : 'I')
      body.append('ot_from_l_three', l3from)
      body.append('ot_to_l_three', l3to)
      body.append('allow_late_in', parseInt(data.late_in) || 0)
      body.append('allow_early_out', parseInt(data.early_out) || 0)
      body.append('multi_in_out', 'N')
      body.append('puch_state_base', 'N')
      body.append('ot_approval_policy', 'I')
      body.append('nessassry_clock_in', 'Y')
      body.append('nessassry_clock_out', 'Y')
      body.append('early_in', 'I')
      body.append('min_early_in', 0)
      body.append('late_out', 'I')
      body.append('min_late_out', 0)
      body.append('calculate_late_out', 'false')
      body.append('calculate_early_in', 'false')
      body.append('calculate_early_in_minumum', 'false')
      body.append('calculate_late_out_minumum', 'false')
      body.append('compensation_duration', 0)
      body.append('day_change_time', '00:00')
      if (data.organization) body.append('organization', data.organization)

      const res = await fetch(`${API_URL}/attendance/timetable/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      })

      if (res.ok) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Timetable **${data.name}** created successfully! Click View to open the timetables list.`,
            data: null,
            link: '/shift/timeTable',
            options: null,
          },
        ])
      } else {
        const errJson = await res.json().catch(() => ({}))
        const msg =
          (errJson.name && errJson.name[0]) ||
          errJson.error ||
          JSON.stringify(errJson) ||
          `Server error ${res.status}`
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Failed to create timetable: ${msg}`,
            data: null,
            link: null,
            options: null,
          },
        ])
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: `Error creating timetable: ${err.message}`,
          data: null,
          link: null,
          options: null,
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  // Always-fresh ref to schedule assignment creation
  const _createScheduleRef = useRef(null)
  _createScheduleRef.current = async function createSchedule(data) {
    setLoading(true)
    try {
      const token = Cookies.get('access_token') || ''
      const res = await fetch(`${API_URL}/chatbot/schedule-create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok) {
        const pageLink = data.sched_type === 'employee' ? '/employee_schedule' : '/branch_schedule'
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: json.message || 'Schedule assigned successfully!',
            data: null,
            link: pageLink,
            options: null,
          },
        ])
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Failed: ${json.error || JSON.stringify(json) || `Server error ${res.status}`}`,
            data: null,
            link: null,
            options: null,
          },
        ])
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: `Error: ${err.message}`, data: null, link: null, options: null },
      ])
    } finally {
      wizardRef.current = null
      setLoading(false)
    }
  }

  // ─── Create Break Time ────────────────────────────────────────────────────
  const _createBreaktimeRef = useRef(null)
  _createBreaktimeRef.current = async function createBreaktime(data) {
    // Auto-compute duration from start_time / end_time / end_cross_day
    let duration = parseInt(data.duration, 10) || 0
    if (!duration && data.start_time && data.end_time) {
      const [sh, sm] = data.start_time.split(':').map(Number)
      const [eh, em] = data.end_time.split(':').map(Number)
      const cross = parseInt(data.end_cross_day || '0', 10)
      duration = Math.max(eh * 60 + em - (sh * 60 + sm) + cross * 24 * 60, 0)
    }
    setLoading(true)
    try {
      const token = Cookies.get('access_token') || ''
      const res = await fetch(`${API_URL}/chatbot/breaktime-create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...data, duration }),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: json.message || 'Break Time created successfully!',
            data: null,
            link: '/shift/breakTime',
            options: null,
          },
        ])
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Failed: ${json.error || JSON.stringify(json)}`,
            data: null,
            link: null,
            options: null,
          },
        ])
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: `Error: ${err.message}`, data: null, link: null, options: null },
      ])
    } finally {
      wizardRef.current = null
      setLoading(false)
    }
  }

  // ─── Create Temporary Schedule ────────────────────────────────────────────
  const _createTempscheduleRef = useRef(null)
  _createTempscheduleRef.current = async function createTempschedule(data) {
    setLoading(true)
    try {
      const token = Cookies.get('access_token') || ''
      const payload = {
        employee_id: data.employee_id,
        timetable: data.timetable,
        start_date: data.start_date,
        end_date: data.end_date === 'skip' || !data.end_date ? data.start_date : data.end_date,
        work_type: data.work_type,
        is_replace_schedule: data.is_replace_schedule,
      }
      const res = await fetch(`${API_URL}/chatbot/tempschedule-create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: json.message || 'Temporary Schedule created successfully!',
            data: null,
            link: '/tempSchedule',
            options: null,
          },
        ])
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: `Failed: ${json.error || JSON.stringify(json)}`,
            data: null,
            link: null,
            options: null,
          },
        ])
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: `Error: ${err.message}`, data: null, link: null, options: null },
      ])
    } finally {
      wizardRef.current = null
      setLoading(false)
    }
  }

  // ─── Leave Issue Diagnostic ───────────────────────────────────────────────
  const _leaveExplainRef = useRef(null)
  _leaveExplainRef.current = async function leaveExplain(data) {
    setLoading(true)
    try {
      const token = Cookies.get('access_token') || ''
      const params = new URLSearchParams({
        employee_id: data.employee_id,
        field: data.issue_field,
        year: data.year,
        ...(data.leave_type_name ? { leave_type_name: data.leave_type_name } : {}),
      })
      const res = await fetch(`${API_URL}/chatbot/leave-explain/?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json().catch(() => ({}))
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: json.message || 'Could not retrieve leave explanation.',
          data: null,
          link: json.valid ? '/detailedLeaveReport' : null,
          options: null,
        },
        {
          role: 'bot',
          text: 'Did this resolve your issue?',
          data: null,
          link: null,
          options: [
            { label: '✅ Yes, resolved!', type: 'leave_issue_resolved_yes' },
            { label: '❌ No, still having issues', type: 'leave_issue_resolved_no' },
          ],
        },
      ])
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: `Error: ${err.message}`, data: null, link: null, options: null },
      ])
    } finally {
      setLoading(false)
    }
  }

  // ─── Payroll Issue Diagnostic ─────────────────────────────────────────────
  const _payrollExplainRef = useRef(null)
  _payrollExplainRef.current = async function payrollExplain(data) {
    setLoading(true)
    try {
      const token = Cookies.get('access_token') || ''
      const params = new URLSearchParams({
        employee_id: data.employee_id,
        field: data.issue_field,
        month_year: data.month_year,
      })
      const res = await fetch(`${API_URL}/chatbot/payroll-explain/?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json().catch(() => ({}))
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: json.message || 'Could not retrieve payroll explanation.',
          data: null,
          link: json.valid ? '/detailedPayrollReports' : null,
          options: null,
        },
        {
          role: 'bot',
          text: 'Did this resolve your issue?',
          data: null,
          link: null,
          options: [
            { label: '✅ Yes, resolved!', type: 'payroll_issue_resolved_yes' },
            { label: '❌ No, still having issues', type: 'payroll_issue_resolved_no' },
          ],
        },
      ])
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: `Error: ${err.message}`, data: null, link: null, options: null },
      ])
    } finally {
      setLoading(false)
    }
  }

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR || !voiceSupported) return
    if (listening) {
      recognitionRef.current?.stop()
      setListening(false)
      return
    }
    const rec = new SR()
    rec.lang = 'en-US'
    rec.interimResults = false
    rec.maxAlternatives = 1
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript
      setInput((prev) => (prev ? prev + ' ' + transcript : transcript))
      setListening(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
    rec.onerror = () => setListening(false)
    rec.onend = () => setListening(false)
    recognitionRef.current = rec
    rec.start()
    setListening(true)
  }

  const sendMessage = useCallback(
    async (overrideText) => {
      const text = (typeof overrideText === 'string' ? overrideText : input).trim()
      if (!text || loading) return
      if (typeof overrideText !== 'string') setInput('')

      setMessages((prev) => [
        ...prev,
        { role: 'user', text, data: null, link: null, options: null },
      ])

      // Wizard mode: route text to wizard step handler instead of chatbot API
      if (wizardRef.current) {
        await _advanceWizardRef.current(text)
        return
      }

      // Support mail multi-step: brief → name → phone
      if (supportMailRef.current) {
        const state = supportMailRef.current
        if (state.step === 'brief') {
          supportMailRef.current = { ...state, step: 'name', brief: text }
          setMessages((prev) => [
            ...prev,
            { role: 'user', text, data: null, link: null, options: null },
            {
              role: 'bot',
              text: 'Thank you. What is your **full name** so we can address you?',
              data: null,
              link: null,
              options: null,
            },
          ])
        } else if (state.step === 'name') {
          supportMailRef.current = { ...state, step: 'phone', name: text }
          setMessages((prev) => [
            ...prev,
            { role: 'user', text, data: null, link: null, options: null },
            {
              role: 'bot',
              text: 'Got it! What is your **phone number** so our team can reach you?',
              data: null,
              link: null,
              options: null,
            },
          ])
        } else if (state.step === 'phone') {
          const { snapshotMsgs, brief, name } = state
          supportMailRef.current = null
          setLoading(true)
          const phone = text
          const allMsgs = [
            ...snapshotMsgs,
            { role: 'user', text: `Issue: ${brief}`, data: null, link: null, options: null },
          ]
          try {
            const token = Cookies.get('access_token') || ''
            const res = await fetch(`${API_URL}/chatbot/send-issue-report/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ messages: allMsgs, contact_name: name, contact_phone: phone }),
            })
            const json = await res.json().catch(() => ({}))
            setMessages((prev) => [
              ...prev,
              { role: 'user', text: phone, data: null, link: null, options: null },
              {
                role: 'bot',
                text: json.sent
                  ? `📧 Your issue has been sent to our support team!\n\n**Name:** ${name}\n**Phone:** ${phone}\n\nWe will contact you soon. Thank you for your patience!`
                  : `⚠️ Could not send the report (${
                      json.error || 'unknown error'
                    }). Please contact support at **suhail@aiviz.me**.`,
                data: null,
                link: null,
                options: [{ label: '🔄 Start New Chat', type: 'leave_restart_chat' }],
              },
            ])
          } catch (err) {
            setMessages((prev) => [
              ...prev,
              { role: 'user', text: phone, data: null, link: null, options: null },
              {
                role: 'bot',
                text: `⚠️ Could not send: ${err.message}. Contact support at **suhail@aiviz.me**.`,
                data: null,
                link: null,
                options: null,
              },
            ])
          } finally {
            setLoading(false)
          }
        }
        return
      }

      // Briefing mode: user is describing their issue before we send the report
      if (awaitingBriefRef.current) {
        const { snapshotMsgs } = awaitingBriefRef.current
        awaitingBriefRef.current = null
        setLoading(true)
        try {
          const token = Cookies.get('access_token') || ''
          // include all prior messages + the briefing the user just typed
          const allMsgs = [
            ...snapshotMsgs,
            { role: 'user', text, data: null, link: null, options: null },
          ]
          const res = await fetch(`${API_URL}/chatbot/send-issue-report/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ messages: allMsgs }),
          })
          const json = await res.json().catch(() => ({}))
          setMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text: json.sent
                ? '📧 A report has been sent to our support team with your description and chat session. We will contact you soon!\n\nThank you for your patience!'
                : `⚠️ Could not send the report automatically (${
                    json.error || 'unknown error'
                  }). Please contact support directly at **suhail@aiviz.me**.`,
              data: null,
              link: null,
              options: [{ label: '🔄 Start New Chat', type: 'leave_restart_chat' }],
            },
          ])
        } catch (err) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text: `⚠️ Could not send the report: ${err.message}. Please contact support at **suhail@aiviz.me**.`,
              data: null,
              link: null,
              options: null,
            },
          ])
        } finally {
          setLoading(false)
        }
        return
      }

      setLoading(true)

      try {
        const token = Cookies.get('access_token') || ''
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 25000)
        let res
        try {
          res = await fetch(`${API_URL}/chatbot/query/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ message: text }),
            signal: controller.signal,
          })
        } finally {
          clearTimeout(timeoutId)
        }

        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.error || `Server error ${res.status}`)
        }

        const json = await res.json()
        if (json.silent) {
          // No access — remove the user message we just added and show nothing
          setMessages((prev) => prev.slice(0, -1))
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: json.answer || 'No answer returned.',
            data: json.data || null,
            link: json.link || null,
            options: json.options || null,
          },
        ])
      } catch (err) {
        const msg =
          err.name === 'AbortError'
            ? "Sorry, I couldn't get a response in time. Please try again."
            : err.message?.includes('NetworkError') || err.message?.includes('fetch')
            ? "I'm having trouble connecting. Please check your connection and try again."
            : `Sorry, something went wrong. Please try again.`
        setMessages((prev) => [
          ...prev,
          { role: 'bot', text: msg, data: null, link: null, options: null },
        ])
      } finally {
        setLoading(false)
      }
    },
    [input, loading],
  )

  const handleOptionClick = useCallback(
    async (opt) => {
      // ── Timetable wizard: user clicked "Create Timetable" start button ──
      if (opt.type === 'wizard' && opt.value === 'start_timetable_wizard') {
        if (!permsRef.current.canCreateTimetable) {
          setMessages((prev) => [
            ...prev,
            { role: 'user', text: 'Create Timetable', data: null, link: null, options: null },
            {
              role: 'bot',
              text: 'You do not have permission to create timetables.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Create Timetable', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'What type of timetable?',
            data: null,
            link: null,
            options: [
              { label: 'Normal', value: 'N', type: 'wizard_type' },
              { label: 'Flexible', value: 'F', type: 'wizard_type' },
            ],
          },
        ])
        return
      }

      // ── Temporary Schedule: user clicked "Create Temporary Schedule" ──
      if (opt.type === 'wizard' && opt.value === 'start_tempschedule_wizard') {
        if (!permsRef.current.canCreateTempschedule) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'user',
              text: opt.label || 'Create Temporary Schedule',
              data: null,
              link: null,
              options: null,
            },
            {
              role: 'bot',
              text: 'You do not have permission to create temporary schedules.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'user',
            text: opt.label || 'Create Temporary Schedule',
            data: null,
            link: null,
            options: null,
          },
          {
            role: 'bot',
            text: 'How would you like to create the temporary schedule?',
            data: null,
            link: null,
            options: [
              { label: 'Create in Chat', type: 'tempschedule_chat', value: 'chat' },
              { label: 'Open Page \u2192', type: 'page', path: '/tempSchedule/add' },
            ],
          },
        ])
        return
      }

      // ── Temporary Schedule: user chose "Create in Chat" ──
      if (opt.type === 'tempschedule_chat') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Create in Chat', data: null, link: null, options: null },
        ])
        wizardRef.current = {
          steps: TEMPSCHEDULE_STEPS,
          stepIdx: 0,
          data: {},
          onComplete: (data) => _createTempscheduleRef.current(data),
        }
        const firstStep = TEMPSCHEDULE_STEPS[0]
        setMessages((prev) => [
          ...prev,
          { role: 'bot', text: stepLabel(firstStep), data: null, link: null, options: null },
        ])
        return
      }

      // ── Break time: user clicked "Create Break Time" ──
      if (opt.type === 'wizard' && opt.value === 'start_breaktime_wizard') {
        if (!permsRef.current.canCreateBreaktime) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'user',
              text: opt.label || 'Create Break Time',
              data: null,
              link: null,
              options: null,
            },
            {
              role: 'bot',
              text: 'You do not have permission to create break times.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'user',
            text: opt.label || 'Create Break Time',
            data: null,
            link: null,
            options: null,
          },
          {
            role: 'bot',
            text: 'How would you like to create the break time?',
            data: null,
            link: null,
            options: [
              { label: 'Create in Chat', type: 'breaktime_chat', value: 'chat' },
              { label: 'Open Page \u2192', type: 'page', path: '/shift/breakTime/add' },
            ],
          },
        ])
        return
      }

      // ── Wizard: in-step option click (OT confirm, cross days, etc.) ──
      if (opt.type === 'wizard') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
        ])
        await _advanceWizardRef.current(opt.value)
        return
      }

      // ── Timetable wizard: user chose Normal / Flexible type ──
      if (opt.type === 'wizard_type') {
        if (!permsRef.current.canCreateTimetable) {
          setMessages((prev) => [
            ...prev,
            { role: 'user', text: opt.label, data: null, link: null, options: null },
            {
              role: 'bot',
              text: 'You do not have permission to create timetables.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        const ttType = opt.value // 'N' or 'F'
        const pagePath =
          ttType === 'N' ? '/shift/timetable/normal/add' : '/shift/timetable/flexible/add'
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'How would you like to create it?',
            data: null,
            link: null,
            options: [
              { label: 'Create in Chat', type: 'timetable_chat', value: ttType },
              { label: 'Open Page \u2192', type: 'page', path: pagePath },
            ],
          },
        ])
        return
      }

      // ── Break time: user chose "Create in Chat" ──
      if (opt.type === 'breaktime_chat') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Create in Chat', data: null, link: null, options: null },
        ])
        const token = Cookies.get('access_token') || ''
        fetch(`${API_URL}/chatbot/user-info/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .catch(() => ({}))
          .then((info) => {
            const steps = BREAKTIME_STEPS
            const firstStep = steps[0]
            wizardRef.current = {
              steps,
              stepIdx: 0,
              data: { organization: info.organization_id || null },
              onComplete: (data) => _createBreaktimeRef.current(data),
            }
            const opts = firstStep.options
              ? firstStep.options.map((o) => ({ ...o, type: 'wizard' }))
              : null
            setMessages((prev) => [
              ...prev,
              { role: 'bot', text: stepLabel(firstStep), data: null, link: null, options: opts },
            ])
          })
        return
      }

      // ── Timetable: user chose "Create in Chat" ──
      if (opt.type === 'timetable_chat') {
        const ttType = opt.value // 'N' or 'F'
        const steps = ttType === 'N' ? TT_NORMAL_STEPS : TT_FLEXIBLE_STEPS
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Create in Chat', data: null, link: null, options: null },
        ])
        // Fetch org ID then start wizard
        const token = Cookies.get('access_token') || ''
        fetch(`${API_URL}/chatbot/user-info/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .catch(() => ({}))
          .then((info) => {
            const firstStep = steps[0]
            wizardRef.current = {
              steps,
              stepIdx: 0,
              data: { type: ttType, organization: info.organization_id || null },
            }
            const opts = firstStep.options
              ? firstStep.options.map((o) => ({ ...o, type: 'wizard' }))
              : null
            setMessages((prev) => [
              ...prev,
              { role: 'bot', text: stepLabel(firstStep), data: null, link: null, options: opts },
            ])
          })
        return
      }

      if (opt.type === 'chat') {
        sendMessage(opt.value)
        return
      }

      // ── Schedule assignment wizard: ask Employee or Branch/Dept ──
      if (opt.type === 'schedule_assign_wizard') {
        if (!permsRef.current.canCreateSchedule) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'user',
              text: opt.label || 'Assign Schedule',
              data: null,
              link: null,
              options: null,
            },
            {
              role: 'bot',
              text: 'You do not have permission to create schedules.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        setMessages((prev) => [
          ...prev,
          {
            role: 'user',
            text: opt.label || 'Assign Schedule',
            data: null,
            link: null,
            options: null,
          },
          {
            role: 'bot',
            text: 'What type of schedule would you like to assign?',
            data: null,
            link: null,
            options: [
              { label: 'Employee Schedule', type: 'schedule_type', value: 'employee' },
              { label: 'Branch / Dept Schedule', type: 'schedule_type', value: 'branch' },
            ],
          },
        ])
        return
      }

      // ── Schedule type chosen: ask page or chat ──
      if (opt.type === 'schedule_type') {
        if (!permsRef.current.canCreateSchedule) {
          setMessages((prev) => [
            ...prev,
            { role: 'user', text: opt.label, data: null, link: null, options: null },
            {
              role: 'bot',
              text: 'You do not have permission to create schedules.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        const schedType = opt.value
        const pagePath =
          schedType === 'employee' ? '/employee_schedule/add' : '/branch_schedule/add'
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'How would you like to proceed?',
            data: null,
            link: null,
            options: [
              { label: 'Open Page \u2192', type: 'page', path: pagePath },
              { label: 'Fill in Chat', type: 'schedule_chat', value: schedType },
            ],
          },
        ])
        return
      }

      // ── Fill in Chat: start schedule wizard ──
      if (opt.type === 'schedule_chat') {
        if (!permsRef.current.canCreateSchedule) {
          setMessages((prev) => [
            ...prev,
            { role: 'user', text: 'Fill in Chat', data: null, link: null, options: null },
            {
              role: 'bot',
              text: 'You do not have permission to create schedules.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        const schedType = opt.value
        const steps = schedType === 'employee' ? SCHEDULE_EMP_STEPS : SCHEDULE_BRANCH_STEPS
        const token = Cookies.get('access_token') || ''
        fetch(`${API_URL}/chatbot/user-info/`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .catch(() => ({}))
          .then((info) => {
            wizardRef.current = {
              steps,
              stepIdx: 0,
              data: { sched_type: schedType, organization: info.organization_id || null },
              onComplete: (data) => _createScheduleRef.current(data),
            }
            setMessages((prev) => [
              ...prev,
              { role: 'user', text: 'Fill in Chat', data: null, link: null, options: null },
              { role: 'bot', text: stepLabel(steps[0]), data: null, link: null, options: null },
            ])
          })
        return
      }

      // ── Reports: user clicked a category (Daily / Monthly / Leave / Exception / Administrative) ──
      if (opt.type === 'report_category') {
        const allPages = REPORT_CATEGORY_PAGES[opt.key] || []
        const pages = allPages.filter((p) => !p.perm || permsRef.current[p.perm])
        const categoryLabel = opt.label.replace(/^[^\w]+/, '').trim() // strip leading emoji
        if (!pages.length) {
          setMessages((prev) => [
            ...prev,
            { role: 'user', text: opt.label, data: null, link: null, options: null },
            {
              role: 'bot',
              text: 'No pages found for this category.',
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `📍 **Reports → ${categoryLabel}**\n\nSelect a report:`,
            data: null,
            link: null,
            options: pages.map((p) => ({
              label: p.label,
              type: 'report_item',
              path: p.path,
              sidebar: p.sidebar,
              desc: p.desc,
              exportExcel: p.exportExcel,
              exportPdf: p.exportPdf,
              categoryLabel,
            })),
          },
        ])
        return
      }

      // ── Reports: user clicked a specific report page ──
      if (opt.type === 'report_item') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text:
              opt.dateParamStyle === 'monthYear' ||
              opt.dateParamStyle === 'monthYear_leave' ||
              opt.dateParamStyle === 'detailedLeave'
                ? `📅 **${opt.label}**\n\nWould you like to filter by period?`
                : `📅 **${opt.label}**\n\nWould you like to filter by date range?`,
            data: null,
            link: null,
            options: [
              {
                label:
                  opt.dateParamStyle === 'monthYear' ||
                  opt.dateParamStyle === 'monthYear_leave' ||
                  opt.dateParamStyle === 'detailedLeave'
                    ? 'Yes, filter by period'
                    : 'Yes, filter by date',
                type: 'report_date_ask',
                path: opt.path,
                labelName: opt.label,
                sidebar: opt.sidebar,
                desc: opt.desc,
                exportExcel: opt.exportExcel,
                exportPdf: opt.exportPdf,
                dateParamStyle: opt.dateParamStyle,
              },
              {
                label: 'No, open without filter',
                type: 'report_date_skip',
                path: opt.path,
                labelName: opt.label,
                sidebar: opt.sidebar,
                desc: opt.desc,
                exportExcel: opt.exportExcel,
                exportPdf: opt.exportPdf,
                dateParamStyle: opt.dateParamStyle,
              },
            ],
          },
        ])
        return
      }

      // ── Reports: user wants date filter ──
      if (opt.type === 'report_date_ask') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Yes, filter by date', data: null, link: null, options: null },
          {
            role: 'bot',
            text:
              opt.dateParamStyle === 'detailedLeave'
                ? `Select filters for **${opt.labelName}**:`
                : opt.dateParamStyle === 'monthYear' || opt.dateParamStyle === 'monthYear_leave'
                ? `Select the month and year for **${opt.labelName}**:`
                : `Select the date range for **${opt.labelName}**:`,
            data: null,
            link: null,
            options: null,
            datePicker: {
              path: opt.path,
              labelName: opt.labelName,
              sidebar: opt.sidebar,
              desc: opt.desc,
              exportExcel: opt.exportExcel,
              exportPdf: opt.exportPdf,
              dateParamStyle: opt.dateParamStyle,
              token: Cookies.get('access_token'),
            },
          },
        ])
        return
      }

      // ── Reports: user skipped date filter ──
      if (opt.type === 'report_date_skip') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'No, open without filter', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `📍 **${opt.labelName}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View →', type: 'report_view', path: opt.path, labelName: opt.labelName },
              {
                label: 'Open in New Tab ↗',
                type: 'report_new_tab',
                path: opt.path,
                labelName: opt.labelName,
              },
              ...(opt.exportExcel
                ? [
                    {
                      label: 'Export Excel 📊',
                      type: 'report_export_excel',
                      path: opt.path,
                      labelName: opt.labelName,
                      exportExcel: opt.exportExcel,
                    },
                  ]
                : []),
              ...(opt.exportPdf
                ? [
                    {
                      label: 'Export PDF 📄',
                      type: 'report_export_pdf',
                      path: opt.path,
                      labelName: opt.labelName,
                      exportPdf: opt.exportPdf,
                    },
                  ]
                : []),
              {
                label: 'Guide 📖',
                type: 'report_guide',
                path: opt.path,
                labelName: opt.labelName,
                desc: opt.desc,
                sidebar: opt.sidebar,
              },
            ],
          },
        ])
        return
      }

      // ── Reports: dates confirmed via DatePickerForm ──
      if (opt.type === 'report_date_confirm') {
        const isMonthYear =
          opt.dateParamStyle === 'monthYear' ||
          opt.dateParamStyle === 'monthYear_leave' ||
          opt.dateParamStyle === 'detailedLeave'
        const dateLabel = isMonthYear
          ? `${MONTHS[parseInt(opt.selectedMonth, 10) - 1]} ${opt.selectedYear}`
          : `${opt.startDate} → ${opt.endDate}`
        const sharedFields = {
          path: opt.path,
          labelName: opt.labelName,
          dateParamStyle: opt.dateParamStyle,
          startDate: opt.startDate,
          endDate: opt.endDate,
          selectedMonth: opt.selectedMonth,
          selectedYear: opt.selectedYear,
          leaveTypes: opt.leaveTypes,
          branches: opt.branches,
        }
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: `Date: ${dateLabel}`, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `📍 **${opt.labelName}**\nPeriod: **${dateLabel}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View →', type: 'report_view', ...sharedFields },
              { label: 'Open in New Tab ↗', type: 'report_new_tab', ...sharedFields },
              ...(opt.exportExcel
                ? [
                    {
                      label: 'Export Excel 📊',
                      type: 'report_export_excel',
                      exportExcel: opt.exportExcel,
                      ...sharedFields,
                    },
                  ]
                : []),
              ...(opt.exportPdf
                ? [
                    {
                      label: 'Export PDF 📄',
                      type: 'report_export_pdf',
                      exportPdf: opt.exportPdf,
                      ...sharedFields,
                    },
                  ]
                : []),
              {
                label: 'Guide 📖',
                type: 'report_guide',
                path: opt.path,
                labelName: opt.labelName,
                desc: opt.desc,
                sidebar: opt.sidebar,
              },
            ],
          },
        ])
        return
      }

      if (opt.type === 'report_view') {
        const queryStr = opt.startDate ? `?from_date=${opt.startDate}&to_date=${opt.endDate}` : ''
        navigate(opt.path + queryStr)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}**…`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'report_new_tab') {
        const queryStr = opt.startDate ? `?from_date=${opt.startDate}&to_date=${opt.endDate}` : ''
        window.open(`/#${opt.path}${queryStr}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'report_export_excel' || opt.type === 'report_export_pdf') {
        const isExcel = opt.type === 'report_export_excel'
        const endpoint = isExcel ? opt.exportExcel : opt.exportPdf
        const label = isExcel ? 'Export Excel' : 'Export PDF'
        const token = Cookies.get('access_token')
        const params = new URLSearchParams()
        const style = opt.dateParamStyle
        if (style === 'detailedLeave') {
          params.set('selectedMonth', opt.selectedMonth)
          params.set('selectedYear', opt.selectedYear)
          if (opt.leaveTypes && opt.leaveTypes.length) {
            opt.leaveTypes.forEach((id) => params.append('leave_type', id))
          }
          if (opt.branches && opt.branches.length) {
            opt.branches.forEach((id) => params.append('branch', id))
          }
        } else if (opt.startDate) {
          if (style === 'monthYear') {
            params.set('selectedMonth', opt.selectedMonth)
            params.set('selectedYear', opt.selectedYear)
            params.set('from_date', opt.startDate)
            params.set('to_date', opt.endDate)
          } else if (style === 'monthYear_leave') {
            params.set('month', opt.selectedMonth)
            params.set('year', opt.selectedYear)
          } else if (style === 'from_date') {
            params.set('from_date', opt.startDate)
            params.set('to_date', opt.endDate)
          } else {
            params.set('fromDate', opt.startDate)
            params.set('toDate', opt.endDate)
          }
        }
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Requesting export for **${opt.labelName}**…`,
            data: null,
            link: null,
            options: null,
          },
        ])
        const qs = params.toString()
        const url = `${API_URL}/${endpoint}${qs ? '?' + qs : ''}`
        // Record the highest existing queue ID before triggering, so we only match NEW items
        let maxIdBefore = 0
        try {
          const preRes = await fetch(`${API_URL}/attendance/export_queue/`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          const preJson = await preRes.json()
          const preItems = preJson.results || preJson
          if (preItems.length) maxIdBefore = Math.max(...preItems.map((i) => i.id))
        } catch (_) {}

        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
          .then(async () => {
            // Show "preparing" message while we poll
            setMessages((prev) => [
              ...prev.slice(0, -1),
              {
                role: 'bot',
                text: `⏳ **${opt.labelName}** export is being prepared…`,
                data: null,
                link: null,
                options: null,
              },
            ])
            // Poll every 3s up to 60s for the completed file — only match items newer than maxIdBefore
            let found = null
            for (let attempt = 0; attempt < 20; attempt++) {
              await new Promise((r) => setTimeout(r, 3000))
              try {
                const qRes = await fetch(`${API_URL}/attendance/export_queue/`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                const qJson = await qRes.json()
                const items = qJson.results || qJson
                found = items.find(
                  (item) => item.id > maxIdBefore && item.export_status === 'complete' && item.file,
                )
                if (found) break
              } catch (_) {}
            }
            const baseUrl = API_URL.replace(/\/api\/?$/, '')
            const fileUrl =
              found && found.file
                ? found.file.startsWith('http')
                  ? found.file
                  : baseUrl + (found.file.startsWith('/') ? found.file : '/media/' + found.file)
                : null
            setMessages((prev) => [
              ...prev.slice(0, -1),
              found
                ? {
                    role: 'bot',
                    text: `✅ **${opt.labelName}** is ready!`,
                    data: null,
                    link: null,
                    options: null,
                    downloadFile: {
                      url: fileUrl,
                      name:
                        found.report_type && found.report_type !== 'None'
                          ? found.report_type
                          : opt.labelName,
                    },
                  }
                : {
                    role: 'bot',
                    text: `✅ **${opt.labelName}** export queued. It may take a moment to process.`,
                    data: null,
                    link: null,
                    options: [
                      {
                        label: 'View Downloads ↓',
                        type: 'report_view',
                        path: '/downloadQueue',
                        labelName: 'Download Queue',
                      },
                    ],
                  },
            ])
          })
          .catch(() => {
            setMessages((prev) => [
              ...prev.slice(0, -1),
              {
                role: 'bot',
                text: `⚠️ Could not start export. Please open the report and export from there.`,
                data: null,
                link: opt.path,
                options: null,
              },
            ])
          })
        return
      }

      if (opt.type === 'report_guide') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}**\n📍 *Sidebar: ${opt.sidebar}*\n\n${opt.desc}`,
            data: null,
            link: opt.path,
            options: null,
          },
        ])
        return
      }

      // ── Support mail flow ──────────────────────────────────────────────────────
      if (opt.type === 'support_mail_start') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: '✉ Contact Support', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**What issue are you facing?**\n\nBefore we send a mail, have you tried searching in the chat? I can answer many questions instantly.`,
            data: null,
            link: null,
            options: [
              { label: 'Yes, issue not resolved', type: 'support_mail_unresolved' },
              { label: 'No, let me search first', type: 'support_mail_search' },
            ],
          },
        ])
        return
      }

      if (opt.type === 'support_mail_search') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'No, let me search first', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Sure! Type your question below and I will try to help. If you still need to reach us, click the ✉ button in the header anytime.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'support_mail_unresolved') {
        const snapshotMsgs = [...messages]
        supportMailRef.current = { step: 'brief', snapshotMsgs }
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Yes, issue not resolved', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Please **briefly describe the issue** you are facing so we can include it in the support mail:`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'payroll_flow_guide') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Payroll Setup Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: PAYROLL_FLOW_GUIDE,
            data: null,
            link: '/payroll_profile',
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'payroll_issues_start') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Report an Issue', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'Is there an issue in the payroll of an employee?',
            data: null,
            link: null,
            options: [
              { label: 'Yes', type: 'payroll_confirm_yes' },
              { label: 'No, just browsing', type: 'payroll_confirm_no' },
            ],
          },
        ])
        return
      }

      if (opt.type === 'payroll_confirm_no') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'No, just browsing', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'No problem! Let me know if you need anything else. Type **help** for more options.',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'payroll_confirm_yes') {
        const firstStep = PAYROLL_ISSUE_STEPS[0]
        wizardRef.current = {
          steps: PAYROLL_ISSUE_STEPS,
          stepIdx: 0,
          data: {},
          onComplete: (data) => _payrollExplainRef.current(data),
        }
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Yes', data: null, link: null, options: null },
          { role: 'bot', text: firstStep.label, data: null, link: null, options: null },
        ])
        return
      }

      if (opt.type === 'payroll_item') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `\uD83D\uDCCD **${opt.sidebar}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View \u2192', type: 'payroll_view', path: opt.path, labelName: opt.label },
              {
                label: 'Open in New Tab \u2197',
                type: 'payroll_new_tab',
                path: opt.path,
                labelName: opt.label,
              },
              {
                label: 'Guide \uD83D\uDCD6',
                type: 'payroll_guide',
                key: opt.key,
                path: opt.path,
                labelName: opt.label,
              },
            ],
          },
        ])
        return
      }

      if (opt.type === 'payroll_view') {
        navigate(opt.path)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}**\u2026`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'payroll_new_tab') {
        window.open(`/#${opt.path}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'payroll_guide') {
        const guide = PAYROLL_GUIDES[opt.key] || `No guide available for **${opt.labelName}**.`
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: guide,
            data: null,
            link: opt.path,
            options: null,
          },
        ])
        return
      }

      // ── Leave option handlers ─────────────────────────────────────────────

      if (opt.type === 'leave_flow_guide') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Leave Setup Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: LEAVE_FLOW_GUIDE,
            data: null,
            link: '/leave_types',
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'leave_issues_start') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Report an Issue', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'Is there a leave issue for an employee?',
            data: null,
            link: null,
            options: [
              { label: 'Yes', type: 'leave_confirm_yes' },
              { label: 'No, just browsing', type: 'leave_confirm_no' },
            ],
          },
        ])
        return
      }

      if (opt.type === 'leave_confirm_no') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'No, just browsing', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'No problem! Let me know if you need anything else. Type **help** for more options.',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'leave_confirm_yes') {
        const firstStep = LEAVE_ISSUE_STEPS[0]
        wizardRef.current = {
          steps: LEAVE_ISSUE_STEPS,
          stepIdx: 0,
          data: {},
          onComplete: (data) => _leaveExplainRef.current(data),
        }
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Yes', data: null, link: null, options: null },
          { role: 'bot', text: firstStep.label, data: null, link: null, options: null },
        ])
        return
      }

      if (opt.type === 'leave_item') {
        const isDetailed = opt.key === 'detailed_leave'
        const extraOptions = isDetailed
          ? [
              {
                label: 'Filter by Employee →',
                type: 'detailed_leave_employee_ask',
                path: opt.path,
                labelName: opt.label,
              },
            ]
          : []
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `📍 **${opt.sidebar}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View →', type: 'leave_view', path: opt.path, labelName: opt.label },
              {
                label: 'Open in New Tab ↗',
                type: 'leave_new_tab',
                path: opt.path,
                labelName: opt.label,
              },
              ...extraOptions,
              {
                label: 'Guide 📖',
                type: 'leave_guide',
                key: opt.key,
                path: opt.path,
                labelName: opt.label,
              },
            ],
          },
        ])
        return
      }

      if (opt.type === 'detailed_leave_employee_ask') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Filter by Employee', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'Please enter the **Employee ID** to filter the Detailed Leave Report:',
            data: null,
            link: null,
            options: null,
          },
        ])
        wizardRef.current = {
          steps: [{ key: 'employee_id', label: 'Employee ID:' }],
          stepIdx: 0,
          data: {},
          onComplete: (data) => {
            wizardRef.current = null
            const empId = data.employee_id.trim()
            const path = `/detailedLeaveReport?employee_id=${encodeURIComponent(empId)}`
            navigate(path)
            setMessages((prev) => [
              ...prev,
              { role: 'user', text: empId, data: null, link: null, options: null },
              {
                role: 'bot',
                text: `Opening **Detailed Leave Report** filtered for employee **${empId}**…`,
                data: null,
                link: null,
                options: [
                  {
                    label: 'Open in New Tab ↗',
                    type: 'leave_new_tab',
                    path,
                    labelName: 'Detailed Leave Report',
                  },
                ],
              },
            ])
          },
        }
        return
      }

      if (opt.type === 'leave_view') {
        navigate(opt.path)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}**…`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'leave_new_tab') {
        window.open(`/#${opt.path}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'leave_guide') {
        const guide = LEAVE_GUIDES[opt.key] || `No guide available for **${opt.labelName}**.`
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: guide,
            data: null,
            link: opt.path,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'leave_issue_resolved_yes') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: '✅ Yes, resolved!', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'Thank you! Glad the issue is resolved. Feel free to start a new chat anytime you need help.',
            data: null,
            link: null,
            options: [{ label: '🔄 Start New Chat', type: 'leave_restart_chat' }],
          },
        ])
        return
      }

      if (opt.type === 'leave_restart_chat') {
        wizardRef.current = null
        supportMailRef.current = null
        awaitingBriefRef.current = null
        setMessages([INITIAL_MESSAGE])
        try {
          localStorage.removeItem(STORAGE_KEY)
        } catch (_) {}
        return
      }

      if (opt.type === 'leave_issue_resolved_no') {
        const snapshotMsgs = messages.filter(
          (m) => !(m.options && m.options.some((o) => o.type === 'leave_issue_resolved_yes')),
        )
        awaitingBriefRef.current = { snapshotMsgs }
        setMessages((prev) => [
          ...prev,
          {
            role: 'user',
            text: '❌ No, still having issues',
            data: null,
            link: null,
            options: null,
          },
          {
            role: 'bot',
            text: 'Please briefly describe the issue you are facing so we can include it in the report:',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'payroll_issue_resolved_yes') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: '✅ Yes, resolved!', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'Thank you! Glad the payroll issue is resolved. Feel free to start a new chat anytime you need help.',
            data: null,
            link: null,
            options: [{ label: '🔄 Start New Chat', type: 'leave_restart_chat' }],
          },
        ])
        return
      }

      if (opt.type === 'payroll_issue_resolved_no') {
        const snapshotMsgs = messages.filter(
          (m) => !(m.options && m.options.some((o) => o.type === 'payroll_issue_resolved_yes')),
        )
        awaitingBriefRef.current = { snapshotMsgs }
        setMessages((prev) => [
          ...prev,
          {
            role: 'user',
            text: '❌ No, still having issues',
            data: null,
            link: null,
            options: null,
          },
          {
            role: 'bot',
            text: 'Please briefly describe the issue you are facing so we can include it in the report:',
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'config_item') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `\uD83D\uDCCD **${opt.sidebar}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View \u2192', type: 'config_view', path: opt.path, labelName: opt.label },
              {
                label: 'Open in New Tab \u2197',
                type: 'config_new_tab',
                path: opt.path,
                labelName: opt.label,
              },
              {
                label: 'Guide \uD83D\uDCD6',
                type: 'config_guide',
                key: opt.key,
                path: opt.path,
                labelName: opt.label,
              },
            ],
          },
        ])
        return
      }

      if (opt.type === 'config_view') {
        navigate(opt.path)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}** configuration\u2026`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'config_new_tab') {
        window.open(`/#${opt.path}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** configuration opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'config_guide') {
        const guide = CONFIG_GUIDES[opt.key] || `No guide available for **${opt.labelName}**.`
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: guide,
            data: null,
            link: opt.path,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'approval_item') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `📍 **Approvals → ${opt.label}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View \u2192', type: 'approval_view', path: opt.path, labelName: opt.label },
              {
                label: 'Open in New Tab \u2197',
                type: 'approval_new_tab',
                path: opt.path,
                labelName: opt.label,
              },
              {
                label: 'Guide \uD83D\uDCD6',
                type: 'approval_guide',
                key: opt.key,
                path: opt.path,
                labelName: opt.label,
              },
            ],
          },
        ])
        return
      }

      if (opt.type === 'approval_view') {
        navigate(opt.path)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}**\u2026`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'approval_new_tab') {
        window.open(`/#${opt.path}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'approval_guide') {
        const guide = APPROVAL_GUIDES[opt.key] || `No guide available for **${opt.labelName}**.`
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: guide,
            data: null,
            link: opt.path,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'workcode_item') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `\uD83D\uDCCD **${opt.sidebar}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View \u2192', type: 'workcode_view', path: opt.path, labelName: opt.label },
              {
                label: 'Open in New Tab \u2197',
                type: 'workcode_new_tab',
                path: opt.path,
                labelName: opt.label,
              },
              {
                label: 'Guide \uD83D\uDCD6',
                type: 'workcode_guide',
                key: opt.key,
                path: opt.path,
                labelName: opt.label,
              },
            ],
          },
        ])
        return
      }

      if (opt.type === 'workcode_view') {
        navigate(opt.path)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}**\u2026`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'workcode_new_tab') {
        window.open(`/#${opt.path}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'workcode_guide') {
        const guide = WORKCODE_GUIDES[opt.key] || `No guide available for **${opt.labelName}**.`
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: guide,
            data: null,
            link: opt.path,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'employee_item') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
          {
            role: 'bot',
            text: `\uD83D\uDCCD **${opt.sidebar}**\n\nWhat would you like to do?`,
            data: null,
            link: null,
            options: [
              { label: 'View \u2192', type: 'employee_view', path: opt.path, labelName: opt.label },
              {
                label: 'Open in New Tab \u2197',
                type: 'employee_new_tab',
                path: opt.path,
                labelName: opt.label,
              },
              {
                label: 'Guide \uD83D\uDCD6',
                type: 'employee_guide',
                key: opt.key,
                path: opt.path,
                labelName: opt.label,
              },
            ],
          },
        ])
        return
      }

      if (opt.type === 'employee_view') {
        navigate(opt.path)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}**\u2026`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'employee_new_tab') {
        window.open(`/#${opt.path}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'employee_guide') {
        const guide = EMPLOYEE_GUIDES[opt.key] || `No guide available for **${opt.labelName}**.`
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Guide', data: null, link: null, options: null },
          {
            role: 'bot',
            text: guide,
            data: null,
            link: opt.path,
            options: null,
          },
        ])
        return
      }

      // ── Attendance status: user clicked Today / Yesterday (all statuses) ──
      if (opt.type === 'attendance_date_pick' || opt.type === 'absent_date_pick') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: opt.label, data: null, link: null, options: null },
        ])
        // opt.value is the full message the backend expects (e.g. "absent today")
        // Use relative words so the backend resolves the date server-side (avoids dayfirst parse bug)
        sendMessage(opt.value)
        return
      }

      // ── Attendance status: user clicked "Specific Date…" ──
      if (opt.type === 'attendance_date_ask' || opt.type === 'absent_date_ask') {
        // opt.value is the "today" variant of the message (e.g. "absent today")
        // We'll replace "today" with the user-entered date
        const baseMsg = opt.value // e.g. "absent today"
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Specific Date…', data: null, link: null, options: null },
          {
            role: 'bot',
            text: 'Please enter the date (e.g. **2026-07-09** or **09/07/2026**):',
            data: null,
            link: null,
            options: null,
          },
        ])
        wizardRef.current = {
          steps: [{ key: 'attendance_date', label: 'Enter date:' }],
          stepIdx: 0,
          data: {},
          onComplete: (data) => {
            wizardRef.current = null
            // Replace the relative word with the user-typed date
            sendMessage(baseMsg.replace(/\b(today|yesterday)\b/, data.attendance_date))
          },
        }
        return
      }

      // ── Absent employees: View on same page ──
      if (opt.type === 'absent_view') {
        navigate(opt.path)
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'View', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `Opening **${opt.labelName}** in Daily Activity…`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      // ── Absent employees: Open in new tab ──
      if (opt.type === 'absent_new_tab') {
        window.open(`/#${opt.path}`, '_blank')
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Open in New Tab', data: null, link: null, options: null },
          {
            role: 'bot',
            text: `**${opt.labelName}** opened in a new tab.`,
            data: null,
            link: null,
            options: null,
          },
        ])
        return
      }

      if (opt.type === 'page' || opt.path) {
        if (opt.perm && !permsRef.current[opt.perm]) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text: `\u26d4 You don\u2019t have permission to access this page. Please contact your administrator.`,
              data: null,
              link: null,
              options: null,
            },
          ])
          return
        }
        const lines = [`Click **View \u2192** to open the **${opt.label}** page.`]
        if (opt.desc) lines.push(opt.desc)
        if (opt.sidebar) lines.push(`**Sidebar**: ${opt.sidebar}`)
        setMessages((prev) => [
          ...prev,
          { role: 'bot', text: lines.join('\n'), data: null, link: opt.path, options: null },
        ])
      } else {
        sendMessage(opt.value)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendMessage],
  )

  const handleRestart = () => {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (_) {}
    wizardRef.current = null
    supportMailRef.current = null
    awaitingBriefRef.current = null
    setMessages([INITIAL_MESSAGE])
    setInput('')
    setSuggestions([])
    setSuggestionIdx(-1)
  }

  const _SUGGESTION_STOPWORDS = new Set([
    'i',
    'a',
    'an',
    'the',
    'is',
    'am',
    'are',
    'was',
    'were',
    'be',
    'been',
    'have',
    'has',
    'had',
    'do',
    'does',
    'did',
    'will',
    'would',
    'can',
    'could',
    'should',
    'may',
    'might',
    'shall',
    'to',
    'of',
    'in',
    'on',
    'at',
    'for',
    'with',
    'about',
    'from',
    'by',
    'and',
    'or',
    'but',
    'not',
    'no',
    'so',
    'my',
    'me',
    'we',
    'us',
    'our',
    'there',
    'this',
    'that',
    'it',
    'its',
    'some',
    'any',
    'what',
    'which',
    'who',
    'when',
    'where',
    'why',
    'how',
    'please',
    'need',
    'want',
    'see',
    'get',
    'give',
    'tell',
    'find',
  ])

  const handleInputChange = (e) => {
    const val = e.target.value
    setInput(val)
    setSuggestionIdx(-1)
    const trimmed = val.trim()
    if (trimmed.length < 1) {
      setSuggestions([])
      return
    }
    // Extract meaningful keywords from input (skip stopwords and very short words)
    const keywords = trimmed
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length >= 3 && !_SUGGESTION_STOPWORDS.has(w))

    if (keywords.length === 0) {
      // Fallback: use full input as a single token
      const lower = trimmed.toLowerCase()
      setSuggestions(SUGGESTIONS.filter((s) => s.toLowerCase().includes(lower)).slice(0, 6))
      return
    }

    // A suggestion matches if it contains ANY of the keywords
    const filtered = SUGGESTIONS.filter((s) => {
      const sl = s.toLowerCase()
      return keywords.some((kw) => sl.includes(kw))
    }).slice(0, 6)

    setSuggestions(filtered)
  }

  const handleSuggestionPick = (s) => {
    setInput(s)
    setSuggestions([])
    setSuggestionIdx(-1)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSuggestionIdx((i) => Math.min(i + 1, suggestions.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSuggestionIdx((i) => Math.max(i - 1, -1))
        return
      }
      if (e.key === 'Escape') {
        setSuggestions([])
        setSuggestionIdx(-1)
        return
      }
      if (e.key === 'Enter' && !e.shiftKey && suggestionIdx >= 0) {
        e.preventDefault()
        handleSuggestionPick(suggestions[suggestionIdx])
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      setSuggestions([])
      setSuggestionIdx(-1)
      sendMessage()
    }
  }

  if (!chatbotEnabled) return null

  return (
    <>
      <style>{`
        @keyframes micPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(229,57,53,0.5); }
          50%       { box-shadow: 0 0 0 6px rgba(229,57,53,0); }
        }
        @keyframes aitimeBounce {
          0%, 78%, 100% { transform: scale(1) translateY(0); }
          81%  { transform: scale(1.1) translateY(-6px); }
          85%  { transform: scale(0.96) translateY(0); }
          88%  { transform: scale(1.06) translateY(-3px); }
          92%  { transform: scale(1) translateY(0); }
        }
        @keyframes aitimeHeaderBounce {
          0%, 88%, 100% { transform: translateY(0); }
          90%  { transform: translateY(-6px); }
          93%  { transform: translateY(0px); }
          95%  { transform: translateY(-3px); }
          97%  { transform: translateY(0); }
        }
        .aitime-bubble { animation: aitimeBounce 10s infinite ease-in-out; }
        .aitime-bubble:hover { animation: none !important; transform: scale(1.06); }
        .aitime-header-logo { animation: aitimeHeaderBounce 10s infinite ease-in-out; }

        /* ── Dark mode overrides ── */
        body.dark-theme .aitime-panel {
          background: #1e2b1a !important;
          border-color: rgba(94,154,49,0.35) !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05) !important;
        }
        body.dark-theme .aitime-header {
          background: linear-gradient(145deg, #2a3d1f 0%, #1e2e14 100%) !important;
          color: #a8e06a !important;
          border-bottom-color: rgba(94,154,49,0.25) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 4px rgba(0,0,0,0.3) !important;
        }
        body.dark-theme .aitime-header .aitime-close-btn,
        body.dark-theme .aitime-header .aitime-restart-btn {
          color: #a8e06a !important;
          border-color: rgba(168,224,106,0.25) !important;
          background: rgba(255,255,255,0.06) !important;
        }
        body.dark-theme .aitime-messages {
          background: #1e2b1a !important;
        }
        body.dark-theme .aitime-footer {
          background: linear-gradient(145deg, #243319 0%, #1a2712 100%) !important;
          border-top-color: rgba(94,154,49,0.2) !important;
        }
        body.dark-theme .aitime-input {
          background: #162010 !important;
          border-color: rgba(94,154,49,0.35) !important;
          color: #e0f0d0 !important;
          box-shadow: inset 1px 1px 4px rgba(0,0,0,0.4) !important;
        }
        body.dark-theme .aitime-input::placeholder { color: #6a8a5a !important; }
        body.dark-theme .aitime-suggestions {
          background: #1e2b1a !important;
          border-color: rgba(94,154,49,0.3) !important;
        }
        body.dark-theme .aitime-suggestion-item { color: #d0e8b0 !important; border-bottom-color: rgba(255,255,255,0.06) !important; }
        body.dark-theme .aitime-suggestion-item-active { background: #2a3d1f !important; color: #a8e06a !important; }

        /* ── Mobile: full-screen bottom sheet ── */
        @media (max-width: 480px) {
          .aitime-panel {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
            border-radius: 0 !important;
            z-index: 10000 !important;
          }
          .aitime-bubble.panel-open {
            display: none !important;
          }
        }

        /* ── Small tablets: wider panel ── */
        @media (min-width: 481px) and (max-width: 640px) {
          .aitime-panel {
            width: calc(100vw - 24px) !important;
            right: 12px !important;
            max-height: calc(100vh - 100px) !important;
          }
        }
      `}</style>

      {/* Drag boundary indicator — visible only while dragging */}
      {dragging && (
        <div
          style={{
            position: 'fixed',
            bottom: S.bubble.bottom,
            right: S.bubble.right,
            width: MAX_OFFSET + 68,
            height: MAX_OFFSET + 68,
            border: '2px dashed rgba(122,193,67,0.7)',
            borderRadius: 18,
            background: 'rgba(122,193,67,0.06)',
            pointerEvents: 'none',
            zIndex: 9997,
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 8,
              left: 12,
              fontSize: 11,
              color: 'rgba(94,154,49,0.85)',
              fontWeight: 600,
              fontFamily: 'inherit',
              pointerEvents: 'none',
            }}
          >
            Drag to reposition
          </span>
        </div>
      )}

      {/* Drag tooltip — shown above the bubble while dragging */}
      {dragging && (
        <div
          style={{
            position: 'fixed',
            bottom: S.bubble.bottom + bubbleOffset.y + 76,
            right: S.bubble.right - bubbleOffset.x - 10,
            background: 'rgba(30,30,30,0.82)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 500,
            padding: '5px 11px',
            borderRadius: 8,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 10001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          }}
        >
          Release to place here
        </div>
      )}

      {/* Floating bubble */}
      <button
        className={`aitime-bubble${open ? ' panel-open' : ''}`}
        style={{
          ...S.bubble,
          bottom: S.bubble.bottom + (open ? 0 : bubbleOffset.y),
          right: S.bubble.right - (open ? 0 : bubbleOffset.x),
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
          transition: dragging ? 'none' : 'bottom 0.25s ease, right 0.25s ease',
        }}
        onPointerDown={onBubblePointerDown}
        title="Ask a question (drag to reposition)"
        aria-label="Open chatbot"
      >
        <RobotMascot size={60} isOpen={open} />
        {open && (
          <span
            style={{
              position: 'absolute',
              top: 44,
              right: 0,
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.18)',
              color: '#fff',
              fontSize: 8,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {'\u2715'}
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={S.panel} className="aitime-panel" role="dialog" aria-label="Chatbot">
          {/* Header */}
          <div style={S.header} className="aitime-header">
            <img
              src={AitimeLogo}
              alt="Aitime"
              className="aitime-header-logo"
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                flexShrink: 0,
                filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.12))',
              }}
            />
            <span style={{ flex: 1 }}>AITIME Assistant</span>
            <span
              style={{
                marginLeft: 4,
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#44c767',
                display: 'inline-block',
                flexShrink: 0,
                boxShadow: '0 0 0 2px rgba(68,199,103,0.25)',
              }}
              title="Online"
            />
            <button
              style={{
                ...S.closeBtn,
                marginLeft: 0,
                fontSize: 12,
                opacity: 0.7,
                padding: '2px 9px',
                border: '1px solid rgba(45,110,26,0.25)',
                borderRadius: 12,
                lineHeight: 1.5,
                background: 'rgba(255,255,255,0.55)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
              className="aitime-restart-btn"
              onClick={handleRestart}
              title="Clear chat history and restart"
              aria-label="Restart chat"
            >
              &#8635; Restart
            </button>
            <button
              style={{
                ...S.closeBtn,
                marginLeft: 0,
                fontSize: 12,
                opacity: 0.7,
                padding: '2px 9px',
                border: '1px solid rgba(45,110,26,0.25)',
                borderRadius: 12,
                lineHeight: 1.5,
                background: 'rgba(255,255,255,0.55)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
              className="aitime-restart-btn"
              onClick={() => handleOptionClick({ type: 'support_mail_start' })}
              title="Contact support via email"
              aria-label="Contact support"
            >
              ✉ Support
            </button>
            <button
              style={S.closeBtn}
              className="aitime-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              {'\u2715'}
            </button>
          </div>

          {/* Messages */}
          <div style={S.messages} className="aitime-messages">
            {messages.map((msg, i) => (
              <Message
                key={i}
                msg={msg}
                onNavigate={handleNavigate}
                onOptionClick={handleOptionClick}
              />
            ))}
            {loading && <div style={S.msgTyping}>{'Thinking\u2026'}</div>}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={S.footer} className="aitime-footer">
            {suggestions.length > 0 && (
              <div style={S.suggestionsBox} className="aitime-suggestions">
                {suggestions.map((s, i) => (
                  <div
                    key={s}
                    className={`aitime-suggestion-item${
                      i === suggestionIdx ? ' aitime-suggestion-item-active' : ''
                    }`}
                    style={{
                      ...S.suggestionItem,
                      ...(i === suggestionIdx ? S.suggestionItemActive : {}),
                      borderBottom: i === suggestions.length - 1 ? 'none' : '1px solid #f0f0f0',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleSuggestionPick(s)
                    }}
                    onMouseEnter={() => setSuggestionIdx(i)}
                  >
                    <span style={{ opacity: 0.45, fontSize: 12 }}>&#128269;</span>
                    {s}
                  </div>
                ))}
              </div>
            )}
            <input
              ref={inputRef}
              className="aitime-input"
              style={S.input}
              placeholder={'Ask about employees, leaves, attendance\u2026'}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setSuggestions([]), 150)}
              maxLength={500}
              disabled={loading}
              aria-label="Type your question"
              autoComplete="off"
            />
            <button
              style={{
                flexShrink: 0,
                width: 36,
                height: 36,
                minWidth: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                cursor: voiceSupported ? 'pointer' : 'default',
                background: listening ? '#e53935' : 'transparent',
                color: listening ? '#fff' : '#5e9a31',
                border: `1.5px solid ${listening ? '#e53935' : '#5e9a31'}`,
                opacity: voiceSupported ? 1 : 0.35,
                animation: listening ? 'micPulse 1s ease-in-out infinite' : 'none',
                boxShadow: 'none',
              }}
              onClick={voiceSupported ? toggleVoice : undefined}
              disabled={loading}
              aria-label={listening ? 'Stop recording' : 'Start voice input'}
              title={
                voiceSupported
                  ? listening
                    ? 'Listening\u2026 click to stop'
                    : 'Voice input'
                  : 'Voice not supported in this browser'
              }
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M5 10a7 7 0 0 0 14 0" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
            <button
              style={{ ...S.sendBtn, opacity: loading || !input.trim() ? 0.5 : 1 }}
              onClick={() => {
                setSuggestions([])
                sendMessage()
              }}
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              {'\u27A4'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
